import { JsonInput as MantineJsonInput } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import type {
    CustomJsonInputFactory,
    CustomJsonInputFormikFactory,
    IProps,
    IJsonInputFormikProps,
} from "./json-input.types";
import { useField } from "formik";
import inputStyles from "../input/input.module.scss";
import styles from "./json-input.module.scss";
import { Icon } from "../icon";
import { kebabCase } from "lodash";

/**
 * JSON input component with optional icon support and validation.
 * @param {IconDefinition} props.icon - Optional icon to display on the left side of the input.
 * @param {boolean} props.activable - Whether the input can be activated.
 * @returns {JSX.Element} The JsonInput component.
 */
const JsonInput = componentWrapper<CustomJsonInputFactory, IProps>(
    "JsonInput",
    ({ icon, activable, ...otherProps }, ref) => {
        return (
            <MantineJsonInput
                ref={ref}
                data-activable={activable}
                // Maintaining consistency with other EdgeTier inputs
                labelProps={{ style: { "--input-label-size": undefined } }}
                classNames={{
                    root: inputStyles["wrapperRoot"],
                    label: inputStyles["label"],
                    input: `${inputStyles["input"]} ${styles["input"]}`,
                    wrapper: inputStyles["inputWrapper"],
                    section: inputStyles["section"],
                }}
                leftSection={icon && <Icon icon={icon} />}
                {...otherProps}
            />
        );
    }
);

/**
 * JSON input component integrated with Formik for form handling and validation.
 * @param {string} props.name - The name of the input field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @returns {JSX.Element} The JsonInputFormik component.
 */
const JsonInputFormik = componentWrapper<CustomJsonInputFormikFactory, IJsonInputFormikProps>(
    "JsonInputFormik",
    ({ name, validate, ...otherProps }, ref) => {
        const [field, meta, helpers] = useField({ name, validate });
        return (
            <JsonInput
                ref={ref}
                data-testid={`json-input-${kebabCase(name)}`}
                {...otherProps}
                error={meta.touched ? meta.error : undefined}
                value={field.value}
                onChange={(value) => helpers.setValue(value)}
            />
        );
    }
);

JsonInput.Formik = JsonInputFormik;

export { JsonInput, JsonInputFormik };
